import React from 'react';
import createDomPurify from 'dompurify';
import {Remarkable} from 'remarkable-v2';
import {linkify} from 'remarkable-v2/linkify';
import Markdoc from '@markdoc/markdoc';

const markdownRenderer = new Remarkable('full', {
  html: true,
  linkTarget: '_blank',
}).use(linkify);

const DOMPurify = createDomPurify();

DOMPurify.addHook('afterSanitizeAttributes', function (node) {
  // set all elements owning target to target=_blank
  if ('target' in node) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener');
  }
});

const DOMPurifyRemoveTargetWhenEmbedded = createDomPurify();

DOMPurifyRemoveTargetWhenEmbedded.addHook(
  'afterSanitizeAttributes',
  function (node) {
    // if the href is a Dashboard link, remove target and rel so it can be handled by
    // widgetLinkAction, and Dashboard can do client side navigation.
    if (
      'target' in node &&
      node.tagName === 'A' &&
      node
        .getAttribute('href')
        ?.startsWith(document.location.ancestorOrigins[0])
    ) {
      node.removeAttribute('target');
    } else {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener');
    }
  },
);

// Matches plain-text URLs. The first capture group accepts either:
// - URLs with an explicit scheme or leading `www.`
// - Bare domains with optional path segments (e.g. `stripe.com/foo`)
// The second capture group collects a trailing punctuation mark so it can be
// reattached without including it in the URL.
const URL_PATTERN =
  /((?:https?:\/\/|www\.)[^\s<>()]+|(?:\b[\w-]+\.)+[a-z]{2,}(?:\/[^\s<>()]*)?)([.,!?;:)]?)/gi;

// Converts plain-text URLs into autolinks before Markdoc parsing. Existing
// Markdown links (`[label](url)`) and HTML links (`<a href="">`) are left as-is.
function shouldSkipAutolink(source: string, offset: number) {
  const preceding = offset > 0 ? source[offset - 1] : undefined;
  const twoBack = offset > 1 ? source[offset - 2] : undefined;

  if (preceding === '(' && twoBack === ']') {
    return true;
  }

  if (preceding === '@') {
    return true;
  }

  if (preceding === '<') {
    return true;
  }

  return false;
}

function linkifyMarkdownText(text: string) {
  return text.replace(
    URL_PATTERN,
    (match, url: string, trailing: string, offset: number, source: string) => {
      if (shouldSkipAutolink(source, offset)) {
        return match;
      }

      const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      return `[${url}](${href})${trailing ?? ''}`;
    },
  );
}

function MarkdownLink(props: React.ComponentProps<'a'>) {
  return <a {...props} target="_blank" rel="noopener" />;
}

export function renderMarkdownInline(
  text: string,
  options?: {removeTargetInLinks?: boolean},
) {
  const {removeTargetInLinks = false} = options || {};

  // Apply link target handling if needed
  if (removeTargetInLinks) {
    return DOMPurifyRemoveTargetWhenEmbedded.sanitize(
      markdownRenderer.renderInline(text),
    );
  }

  return DOMPurify.sanitize(markdownRenderer.renderInline(text));
}

export function renderMarkdownAsHtml(text: string) {
  const ast = Markdoc.parse(linkifyMarkdownText(text));
  const content = Markdoc.transform(ast);
  return DOMPurify.sanitize(Markdoc.renderers.html(content));
}

export function renderMarkdownAsReact(text: string) {
  const ast = Markdoc.parse(linkifyMarkdownText(text));
  const content = Markdoc.transform(ast);
  return Markdoc.renderers.react(content, React, {
    components: {
      link: MarkdownLink,
    },
  });
}

// TODO: cleanup after ir-ochre-adhere
// adding this function back for a cleaner revert
export function renderMarkdown(text: string) {
  return DOMPurify.sanitize(markdownRenderer.render(text));
}
